/**
 * 確認ダイアログシステムのZustandストア
 */

import { create } from 'zustand';
import { ReactNode } from 'react';

/**
 * ボタンのスタイル
 */
export type ButtonStyle = 'primary' | 'secondary' | 'danger';

/**
 * 確認ダイアログのオプション
 */
export interface ConfirmDialogOptions {
  /** ダイアログのタイトル */
  title?: string;
  /** メインメッセージ */
  message: string;
  /** 詳細情報（オプション） */
  details?: string;
  /** 依存関係情報など追加コンテンツ（オプション） */
  extraContent?: ReactNode;
  /** 確認ボタンのラベル（デフォルト: "OK"） */
  confirmLabel?: string;
  /** キャンセルボタンのラベル（デフォルト: "Cancel"） */
  cancelLabel?: string;
  /** 確認ボタンのスタイル（デフォルト: "primary"） */
  confirmStyle?: ButtonStyle;
  /** テスト用のdata-testid */
  testId?: string;
}

/**
 * 内部で使用する確認ダイアログの状態
 */
interface ConfirmDialogState {
  /** ダイアログが表示されているか */
  isOpen: boolean;
  /** ダイアログのオプション */
  options: ConfirmDialogOptions | null;
  /** ユーザーの選択を解決するためのコールバック */
  resolve: ((value: boolean) => void) | null;
}

/**
 * 確認ダイアログストアのアクション
 */
interface ConfirmDialogActions {
  /** 確認ダイアログを表示し、ユーザーの選択を待つ */
  confirm: (options: ConfirmDialogOptions | string) => Promise<boolean>;
  /** ダイアログを閉じて結果を返す */
  close: (result: boolean) => void;
}

/**
 * 確認ダイアログストア
 */
export const useConfirmDialogStore = create<ConfirmDialogState & ConfirmDialogActions>((set, get) => ({
  // 状態
  isOpen: false,
  options: null,
  resolve: null,

  // アクション
  confirm: (options) => {
    // 文字列の場合はメッセージとして扱う
    const dialogOptions: ConfirmDialogOptions = typeof options === 'string'
      ? { message: options }
      : options;

    return new Promise<boolean>((resolve) => {
      set({
        isOpen: true,
        options: {
          confirmLabel: 'OK',
          cancelLabel: 'Cancel',
          confirmStyle: 'primary',
          ...dialogOptions
        },
        resolve
      });
    });
  },

  close: (result) => {
    const { resolve } = get();

    // ダイアログを閉じる
    set({
      isOpen: false,
      options: null,
      resolve: null
    });

    // Promiseを解決
    if (resolve) {
      resolve(result);
    }
  }
}));
